import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formMasterAPI, commonAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { format, startOfWeek, addDays, parseISO } from 'date-fns';

const Attendance = () => {
  const [searchParams] = useSearchParams();
  const [classId, setClassId] = useState(searchParams.get('class') || '');
  const [academicSessions, setAcademicSessions] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [myClasses, setMyClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [viewMode, setViewMode] = useState('single');
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [weekStart, setWeekStart] = useState(
    format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  );

  const [singleRoster, setSingleRoster] = useState([]);
  const [weekRoster, setWeekRoster] = useState({});
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const classesRes = await formMasterAPI.getMyClasses();
      const sessionsRes = await formMasterAPI.getAcademicSessions();
      const classesData = classesRes?.data || classesRes || [];
      const sessionsData = sessionsRes?.data || sessionsRes || [];
      setMyClasses(Array.isArray(classesData) ? classesData : []);
      setAcademicSessions(Array.isArray(sessionsData) ? sessionsData : []);
      if (Array.isArray(classesData) && classesData.length > 0 && !classId) {
        setClassId(classesData[0].class_id);
      }
      if (Array.isArray(sessionsData) && sessionsData.length > 0 && !selectedSession) {
        setSelectedSession(sessionsData[0].id);
        handleSessionChange(sessionsData[0].id);
      }
    } catch (error) {
      console.error('Error in fetchInitialData:', error);
      toast.error('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const handleSessionChange = async (sessionId) => {
    setSelectedSession(sessionId);
    setSelectedTerm('');
    try {
      const selectedSessionData = academicSessions.find(s => s.id == sessionId);
      if (selectedSessionData?.terms) {
        setTerms(selectedSessionData.terms);
        if (selectedSessionData.terms.length > 0) {
          setSelectedTerm(selectedSessionData.terms[0].id);
        }
      } else {
        const response = await commonAPI.getTerms(sessionId);
        const termsData = response?.data || response || [];
        setTerms(termsData);
        if (termsData.length > 0) {
          setSelectedTerm(termsData[0].id);
        }
      }
    } catch (error) {
      console.error('Failed to load terms:', error);
      toast.error('Failed to load terms');
    }
  };

  const fetchSingleDay = async () => {
    if (!classId || !selectedDate) {
      toast.error('Please select class and date');
      return;
    }
    try {
      setLoading(true);
      const response = await formMasterAPI.getAttendanceRoster(classId, selectedDate);
      const data = response?.data || response || {};
      const roster = data.roster || [];
      setSingleRoster(
        roster.map(item => ({
          student_id: item.student_id,
          name: item.name,
          reg: item.reg,
          status: item.status || 'present',
          remark: item.remark || '',
        }))
      );
      setHasLoaded(true);
      toast.success(`Loaded ${roster.length} students`);
    } catch (error) {
      console.error('Failed to load attendance:', error);
      toast.error(error.response?.data?.detail || 'Failed to load attendance');
      setSingleRoster([]);
      setHasLoaded(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchWeek = async () => {
    if (!classId || !weekStart) {
      toast.error('Please select class and week');
      return;
    }
    const startDate = weekStart;
    const endDate = format(addDays(parseISO(weekStart), 4), 'yyyy-MM-dd');
    try {
      setLoading(true);
      const response = await formMasterAPI.getAttendanceRosterWeek(classId, startDate, endDate);
      const data = response?.data || response || {};
      const weekData = data.week || [];
      const rosterByDate = {};
      weekData.forEach(day => {
        rosterByDate[day.date] = day.roster.map(item => ({
          student_id: item.student_id,
          name: item.name,
          reg: item.reg,
          status: item.status || 'present',
          remark: item.remark || '',
        }));
      });
      setWeekRoster(rosterByDate);
      setHasLoaded(true);
      toast.success(`Loaded week data`);
    } catch (error) {
      console.error('Failed to load week roster:', error);
      toast.error(error.response?.data?.detail || 'Failed to load week roster');
      setWeekRoster({});
      setHasLoaded(false);
    } finally {
      setLoading(false);
    }
  };

  // FIX: convert studentId to Number for correct comparison
  const updateSingleStudent = (studentId, field, value) => {
    const id = Number(studentId);
    setSingleRoster(prev =>
      prev.map(s => (s.student_id === id ? { ...s, [field]: value } : s))
    );
  };

  const updateWeekStudent = (studentId, date, field, value) => {
    const id = Number(studentId);
    setWeekRoster(prev => {
      const newRoster = { ...prev };
      if (newRoster[date]) {
        newRoster[date] = newRoster[date].map(s =>
          s.student_id === id ? { ...s, [field]: value } : s
        );
      }
      return newRoster;
    });
  };

  const handleSaveSingle = async () => {
    if (!classId || !selectedDate) return;
    const marks = singleRoster.map(s => ({
      student_id: s.student_id,
      status: s.status || 'present',
      remark: s.remark || '',
    }));
    if (marks.length === 0) {
      toast.error('No students to save');
      return;
    }
    setSaving(true);
    try {
      await formMasterAPI.markAttendance(classId, { date: selectedDate, marks });
      toast.success('Attendance saved successfully');
      await fetchSingleDay();
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWeek = async () => {
    if (!classId) return;
    const studentIds = Object.values(weekRoster)
      .flatMap(roster => roster.map(s => s.student_id));
    const uniqueStudentIds = [...new Set(studentIds)];
    const dates = Object.keys(weekRoster).sort();
    const marks = uniqueStudentIds.map(studentId => {
      const statuses = {};
      const remarks = {};
      dates.forEach(date => {
        const roster = weekRoster[date] || [];
        const student = roster.find(s => s.student_id === studentId);
        if (student) {
          statuses[date] = student.status || 'present';
          if (student.remark) remarks[date] = student.remark;
        }
      });
      return { student_id: studentId, statuses, remarks };
    });
    const validMarks = marks.filter(m => Object.keys(m.statuses).length > 0);
    if (validMarks.length === 0) {
      toast.error('No attendance data to save');
      return;
    }
    setSaving(true);
    try {
      await formMasterAPI.markAttendanceWeek(classId, { dates, marks: validMarks });
      toast.success('Week attendance saved successfully');
      await fetchWeek();
    } catch (error) {
      console.error('Save week failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to save week');
    } finally {
      setSaving(false);
    }
  };

  const getCurrentClass = () => myClasses.find(cls => cls.class_id == classId);

  const statusOptions = [
    { value: 'present', label: 'Present' },
    { value: 'absent', label: 'Absent' },
    { value: 'late', label: 'Late' },
    { value: 'excused', label: 'Excused' },
  ];

  const renderStatusSelect = (studentId, currentStatus, onChange) => (
    <select
      value={currentStatus || 'present'}
      onChange={e => onChange(studentId, 'status', e.target.value)}
      className="w-full px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
    >
      {statusOptions.map(opt => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );

  const renderRemarkInput = (studentId, currentRemark, onChange) => (
    <input
      type="text"
      value={currentRemark || ''}
      onChange={e => onChange(studentId, 'remark', e.target.value)}
      placeholder="Remark"
      className="w-full px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
    />
  );

  const renderSingleTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Registration</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remark</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {singleRoster.map((student, index) => (
            <tr key={student.student_id} className="hover:bg-gray-50">
              <td className="px-6 py-4 text-sm text-gray-500">{index + 1}</td>
              <td className="px-6 py-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="fas fa-user text-blue-600"></i>
                  </div>
                  <div className="ml-3 text-sm font-medium text-gray-900">{student.name}</div>
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-500">{student.reg}</td>
              <td className="px-6 py-4">
                {renderStatusSelect(student.student_id, student.status, updateSingleStudent)}
              </td>
              <td className="px-6 py-4">
                {renderRemarkInput(student.student_id, student.remark, updateSingleStudent)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderWeekTable = () => {
    const dates = Object.keys(weekRoster).sort();
    if (dates.length === 0)
      return <p className="text-center py-8 text-gray-500">No week data loaded.</p>;

    const studentMap = {};
    dates.forEach(date => {
      (weekRoster[date] || []).forEach(s => {
        if (!studentMap[s.student_id]) {
          studentMap[s.student_id] = {
            name: s.name,
            reg: s.reg,
            statuses: {},
            remarks: {},
          };
        }
        studentMap[s.student_id].statuses[date] = s.status || 'present';
        if (s.remark) studentMap[s.student_id].remarks[date] = s.remark;
      });
    });

    const studentIds = Object.keys(studentMap);

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reg</th>
              {dates.map(date => (
                <th key={date} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                  {format(parseISO(date), 'EEE dd')}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {studentIds.map(studentId => {
              const student = studentMap[studentId];
              const numericId = Number(studentId); // ← ensure number
              return (
                <tr key={numericId} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{student.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{student.reg}</td>
                  {dates.map(date => (
                    <td key={date} className="px-4 py-3 text-center">
                      <div className="flex flex-col items-center space-y-1">
                        {renderStatusSelect(
                          numericId,
                          student.statuses[date],
                          (sid, field, value) => updateWeekStudent(sid, date, field, value)
                        )}
                        {renderRemarkInput(
                          numericId,
                          student.remarks[date] || '',
                          (sid, field, value) => updateWeekStudent(sid, date, field, value)
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Attendance Management</h1>
          <p className="text-gray-600">Mark attendance for your form class</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center space-x-2 bg-gray-100 p-1 rounded-lg">
            <button
              onClick={() => setViewMode('single')}
              className={`px-3 py-1 rounded-md text-sm font-medium ${
                viewMode === 'single' ? 'bg-white shadow' : 'hover:bg-gray-200'
              }`}
            >
              Single Day
            </button>
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1 rounded-md text-sm font-medium ${
                viewMode === 'week' ? 'bg-white shadow' : 'hover:bg-gray-200'
              }`}
            >
              Week View
            </button>
          </div>
          <button
            onClick={viewMode === 'single' ? fetchSingleDay : fetchWeek}
            disabled={!classId || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
          >
            <i className="fas fa-sync mr-2"></i>
            Load {viewMode === 'single' ? 'Roster' : 'Week'}
          </button>
          <button
            onClick={viewMode === 'single' ? handleSaveSingle : handleSaveWeek}
            disabled={!hasLoaded || saving}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <><i className="fas fa-spinner fa-spin mr-2"></i>Saving...</>
            ) : (
              <><i className="fas fa-save mr-2"></i>Save Attendance</>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Class *</label>
            <select
              value={classId}
              onChange={e => setClassId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="">Choose a class</option>
              {myClasses.map(cls => (
                <option key={cls.class_id} value={cls.class_id}>
                  {cls.class_name} ({cls.student_count} students)
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Academic Session</label>
            <select
              value={selectedSession}
              onChange={e => handleSessionChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            >
              <option value="">Select Session</option>
              {academicSessions.map(s => (
                <option key={s.id} value={s.id}>{s.academic_session}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
            <select
              value={selectedTerm}
              onChange={e => setSelectedTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={!selectedSession || loading}
            >
              <option value="">Select Term</option>
              {terms.map(t => (
                <option key={t.id} value={t.id}>{t.term}</option>
              ))}
            </select>
          </div>
          {viewMode === 'single' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                value={selectedDate}
                onChange={e => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Week Starting (Monday)</label>
              <input
                type="date"
                value={weekStart}
                onChange={e => setWeekStart(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
        </div>

        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-blue-700">
            {viewMode === 'single' ? (
              <>Attendance for <strong>{getCurrentClass()?.class_name}</strong> on{' '}
              <strong>{format(parseISO(selectedDate), 'dd/MM/yyyy')}</strong></>
            ) : (
              <>Week attendance for <strong>{getCurrentClass()?.class_name}</strong> starting{' '}
              <strong>{format(parseISO(weekStart), 'dd/MM/yyyy')}</strong></>
            )}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-800">
            {viewMode === 'single' ? 'Student Attendance' : 'Week Attendance Matrix'}
          </h3>
        </div>
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        ) : hasLoaded ? (
          viewMode === 'single' ? renderSingleTable() : renderWeekTable()
        ) : (
          <div className="text-center py-12">
            <i className="fas fa-clipboard-list text-4xl text-gray-300 mb-4"></i>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Attendance Data</h3>
            <p className="text-gray-500">Select a class and date/week, then click "Load".</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Attendance;